import { Check } from "lucide-react";
import { useId } from "react";

import {
  useThemePreference,
  type ThemePreference,
} from "../features/theme/theme";
import { cn } from "../utils/cn";

const OPTIONS: { value: ThemePreference; label: string }[] = [
  { value: "dark", label: "Dark" },
  { value: "light", label: "Light" },
  { value: "system", label: "Follow phone" },
];

/**
 * The choice between Dusk, the light twin, and whatever the phone says.
 *
 * Three native radio buttons in a radiogroup, so the browser supplies the arrow
 * keys, the single tab stop and the checked state a screen reader announces. Each
 * label is the whole 44px target. The checked option carries a tick and a heavier
 * border as well as a different fill, so it reads without colour.
 */
export function ThemeSwitch({ className }: { className?: string }) {
  const [preference, setPreference] = useThemePreference();
  const name = useId();

  return (
    <div
      role="radiogroup"
      aria-label="Colour theme"
      data-testid="theme-switch"
      className={cn("flex flex-col gap-2 sm:flex-row", className)}
    >
      {OPTIONS.map((option) => {
        const checked = preference === option.value;
        return (
          <label
            key={option.value}
            className={cn(
              "flex min-h-11 flex-1 cursor-pointer items-center justify-center gap-2 rounded-md border px-4 py-2 text-sm transition-colors",
              "has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-2 has-[:focus-visible]:ring-offset-background",
              checked
                ? "border-lit bg-secondary font-semibold text-foreground outline outline-1 outline-offset-0 outline-lit"
                : "border-border bg-background font-medium text-muted-foreground hover:border-lit hover:text-foreground",
            )}
            data-testid={`theme-option-${option.value}`}
          >
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={checked}
              onChange={() => setPreference(option.value)}
              className="sr-only"
            />
            {checked && <Check className="h-4 w-4 shrink-0" aria-hidden />}
            {option.label}
          </label>
        );
      })}
    </div>
  );
}
